// React imports
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Third-party imports
import { useMediaQuery } from '@material-ui/core';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

// Types
import { Theme } from '@material-ui/core/styles';
import { Promocode, SavePromocode } from '@spike/promocode-model';

// Actions
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';

// Hooks
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import { usePromoCodes } from './PromoCodesProvider';

// Routes
import { MarketingPath } from 'routes/Paths';

// UI Components
import { ConfirmDialog, Spinner, OverFullWindow } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import DescriptionText from 'components/UI/V3/DescriptionText';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import { SelectionBar } from 'components/UI/SelectionBar/SelectionBar';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';

// Local Components
import { AddPromoCode } from './Add/AddPromoCode';
import PromoCodesTable from './List/PromoCodesTable';
import PromoCodesUsage from './Usage/PromoCodesUsage';

// Styles
import styles from './PromoCodesComponent.module.scss';

interface Props {
    create?: string;
    serviceId?: number;
    onBack?: () => void;
}

export const PromoCodesComponent: FunctionComponent<Props> = props => {
    const {
        promoCodes,
        isFirstRender,
        selectedFilter,
        filterChangeHandler,
        searchText,
        searchTextChangeHandler,
        showAddPromoCode,
        editPromoCode,
        openAddForm,
        openEditForm,
        closeForm,
        showUsage,
        openUsageModal,
        closeUsageModal,
        activatePromoCode,
        deactivatePromoCode
    } = usePromoCodes();

    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const hasPremiumPlan = useHasPremiumPlan();
    const history = useHistory();

    const [showWarning, setShowWarning] = useState(false);

    const [successPromoCode, setSuccessPromoCode] = useState<SavePromocode | null>(null);

    const [showConfirm, setShowConfirm] = useState({ show: false, action: '', promoCode: null as Promocode | null });

    useHideHubSpotChat(Boolean(editPromoCode?.id) || showAddPromoCode);

    /* Scrolling fixing when moving across different components */
    useEffect(() => {
        if (showUsage?.id || showAddPromoCode || editPromoCode?.id) {
            const mainContainer = document.querySelector('#page-content');
            if (mainContainer) {
                mainContainer.scrollTop = 0;
            }
        }
    }, [showUsage?.id, showAddPromoCode, editPromoCode?.id]);

    const handleCloseWarning = () => {
        setShowWarning(false);
        history.push(MarketingPath);
    };

    const filterOptions = [
        { id: 'active', name: 'Active' },
        { id: 'inactive', name: 'Inactive' },
        { id: 'all', name: 'All' }
    ];

    const handleSelect = (id: string) => {
        filterChangeHandler(id);
    };

    const handleUsage = (promoCode: Promocode) => {
        openUsageModal(promoCode);
    };
    const handleBack = () => {
        closeUsageModal();
    };

    const handleEdit = (promoCode: Promocode) => {
        openEditForm(promoCode);
    };

    const requestConfirmation = (action: string, promoCode: Promocode) => {
        setShowConfirm({ show: true, action, promoCode });
    };
    const handleDeactivate = async (promoCode: Promocode) => {
        try {
            await deactivatePromoCode(promoCode);
            dispatch(showSuccessThunk(`${promoCode.name} has been deactivated successfully`));
            setShowConfirm({ show: false, action: '', promoCode: null });
        } catch (error) {
            dispatch(showErrorThunk('Failed to deactivate promo code. Please try again.'));
        }
    };

    const handleActivate = async (promoCode: Promocode) => {
        try {
            await activatePromoCode(promoCode);
            dispatch(showSuccessThunk(`${promoCode.name} has been activated successfully`));
            setShowConfirm({ show: false, action: '', promoCode: null });
        } catch (error) {
            dispatch(showErrorThunk('Failed to activate promo code. Please try again.'));
        }
    };

    const handlePromoCodeSuccess = (savedPromoCode: SavePromocode) => {
        setSuccessPromoCode(savedPromoCode);
    };

    const handleSuccessClose = () => {
        setSuccessPromoCode(null);
        closeForm();
    };

    const renderContent = () => {
        // Only show full-screen spinner for initial load
        if (isFirstRender) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        if (successPromoCode) {
            return (
                <OverFullWindow
                    showMobileLogo
                    title="Adding Promo Code"
                    onClose={handleSuccessClose}
                >
                    <SuccessScreen
                        onClick={handleSuccessClose}
                        title={'Good Job'}
                        message={
                            <SuccessMessage
                                promoName={successPromoCode.name}
                                isEditing={Boolean(editPromoCode?.id)}
                            />
                        }
                        buttonLabel={'Go to Promo Codes'}
                    />
                </OverFullWindow>
            );
        }
        if (showAddPromoCode) {
            return (
                <AddPromoCode
                    onClose={closeForm}
                    onSuccess={handlePromoCodeSuccess}
                />
            );
        }
        if (editPromoCode?.id) {
            return (
                <AddPromoCode
                    onClose={closeForm}
                    onSuccess={handlePromoCodeSuccess}
                    promoCode={editPromoCode}
                    isEditing={true}
                />
            );
        }

        return (
            <div className={styles.wrapperContainer}>
                {promoCodes && (
                    <>
                        <ConfirmDialog
                            open={showConfirm.show}
                            title={showConfirm.action === 'Activate' ? 'Activate Promo Code' : 'Deactivate Promo Code'}
                            question={
                                showConfirm.action === 'Activate'
                                    ? 'Are you sure you want to activate this promo code?'
                                    : 'Are you sure you want to deactivate this promo code?'
                            }
                            onCancel={() => setShowConfirm({ show: false, action: '', promoCode: null })}
                            actions={
                                <>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="black"
                                        label="Cancel"
                                        style={{ minWidth: 'unset' }}
                                        onClick={() => setShowConfirm({ show: false, action: '', promoCode: null })}
                                    />

                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="danger"
                                        label={showConfirm.action === 'Activate' ? 'Yes, Activate' : 'Yes, Deactivate'}
                                        style={{ margin: 0, minWidth: 'unset' }}
                                        onClick={() =>
                                            showConfirm.action === 'Activate'
                                                ? handleActivate(showConfirm.promoCode!)
                                                : handleDeactivate(showConfirm.promoCode!)
                                        }
                                    />
                                </>
                            }
                        />
                        <div className={styles.headWrapper}>
                            <DefaultHeader
                                title={'Promo Codes'}
                                buttonLabel={isMobile ? 'Add' : 'Add Promo Code'}
                                displayButton={true}
                                onClick={openAddForm}
                                id="promocode_add_button"
                                className={styles.headerContainer}
                                buttonIcon={faPlus}
                            />
                            <DescriptionText
                                className={styles.label}
                                text="Create promo codes to attract new clients, boost bookings, or celebrate a special
                                occasion."
                            ></DescriptionText>

                            <SelectionBar
                                options={filterOptions}
                                selectedId={selectedFilter}
                                onSelect={handleSelect}
                                className={styles.selectionBar}
                            />

                            <SearchBar
                                searchText={searchText}
                                onChangeSearchText={searchTextChangeHandler}
                                placeholder="Search by promo code name"
                                className={styles.searchBarContainer}
                            />
                        </div>
                        <div className={styles.fullWidthContainer}>
                            <PromoCodesTable
                                onEdit={handleEdit}
                                onDeactivate={promoCode => requestConfirmation('Deactivate', promoCode)}
                                onActivate={promoCode => requestConfirmation('Activate', promoCode)}
                                onUsage={handleUsage}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={styles.root}>
            {showWarning ? (
                <PremiumFeatureAlert
                    isOpen={showWarning}
                    onCancel={handleCloseWarning}
                    text={
                        !hasPremiumPlan
                            ? 'Unlock Promo Codes with a premium plan'
                            : 'Promo Codes require an active CuddlesPay account'
                    }
                />
            ) : showUsage ? (
                <PromoCodesUsage onBack={handleBack} />
            ) : (
                renderContent()
            )}
        </div>
    );
};

const SuccessMessage = ({ promoName, isEditing }: { promoName: string; isEditing: boolean }) => {
    return (
        <div style={{ textAlign: 'center' }}>
            <span style={{ fontWeight: 'bold' }}>{promoName}</span>
            <span>{` was successfully ${isEditing ? 'edited.' : 'created.'}`}</span>
        </div>
    );
};

export default PromoCodesComponent;
